import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import { useCallback } from 'react'

import { useRemoteData } from '~/features/performance/useRemoteData'
import { getLesson } from '~/services/lessons'

import {
  type LessonNetwork,
  attendance,
  bookingLabels,
  classroomLabels,
  followUp,
  networkLabels,
  summaryLabels,
  typeLabels,
} from './model'

export const formatTime = (value: number | null) =>
  value == null ? '—' : dayjs(value).format('YYYY-MM-DD HH:mm:ss')
const duration = (seconds: number | null) =>
  seconds == null
    ? '无数据'
    : `${Math.floor(seconds / 60)} 分 ${seconds % 60} 秒`

export default function LessonDetail({ id }: { id: string }) {
  const request = useCallback(() => getLesson(id), [id])
  const { data, loading, error, retry } = useRemoteData(request)
  if (loading) return <Spin />
  if (error || !data)
    return (
      <Alert
        type="error"
        showIcon
        message="课程详情加载失败"
        action={<Button onClick={retry}>重试</Button>}
      />
    )
  const { lesson } = data
  return (
    <Space direction="vertical" size="large" style={{ display: 'flex' }}>
      <Alert type="info" showIcon message={followUp(lesson)} />
      <Descriptions
        bordered
        size="small"
        column={2}
        title={`课程 / 预约 #${lesson.id}`}
      >
        <Descriptions.Item label="计划上课时间">
          {formatTime(lesson.classStartTime)}
        </Descriptions.Item>
        <Descriptions.Item label="课程类型">
          {typeLabels[lesson.type] ?? lesson.type}
        </Descriptions.Item>
        <Descriptions.Item label="预约状态">
          {bookingLabels[lesson.bookingStatus] ?? lesson.bookingStatus}
        </Descriptions.Item>
        <Descriptions.Item label="课堂状态">
          {classroomLabels[lesson.classroomStatus]}
        </Descriptions.Item>
        <Descriptions.Item label="老师">
          {lesson.teacher.name ?? '已删除老师'}（ID: {lesson.teacher.id}）
        </Descriptions.Item>
        <Descriptions.Item label="学生">
          {lesson.student.name ?? '已删除学生'}（ID: {lesson.student.id}）
        </Descriptions.Item>
        {[
          { label: '老师联系方式', person: lesson.teacher },
          { label: '学生联系方式', person: lesson.student },
        ].map(({ label, person }) => {
          return (
            <Descriptions.Item key={label} label={label}>
              <Space direction="vertical" size={0}>
                <Typography.Text copyable={!!person.phone}>
                  {person.phone || '未填写电话'}
                </Typography.Text>
                <Typography.Text copyable={!!person.email}>
                  {person.email || '未填写邮箱'}
                </Typography.Text>
              </Space>
            </Descriptions.Item>
          )
        })}
        <Descriptions.Item label="期望申请截止">
          {formatTime(lesson.desiredExpiresAt)}
        </Descriptions.Item>
        <Descriptions.Item label="申请来源">
          {lesson.requestedByAdminUserId
            ? `管理员代发（${lesson.requestedByAdminUserId}）`
            : '普通预约 / 申请'}
        </Descriptions.Item>
        <Descriptions.Item label="取消时间">
          {formatTime(lesson.cancelTime)}
        </Descriptions.Item>
        <Descriptions.Item label="取消 / 拒绝原因">
          {lesson.cancelReason || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="备注" span={2}>
          {lesson.remark || '—'}
        </Descriptions.Item>
      </Descriptions>
      {lesson.classroomStatus !== 'UPCOMING' && (
        <>
          <Card title="出勤与课堂" size="small">
            <Table
              pagination={false}
              size="small"
              rowKey="role"
              dataSource={(['teacher', 'student'] as const).map((role) => ({
                role,
                name: role === 'teacher' ? '老师' : '学生',
                ...attendance(lesson, role),
                joined:
                  role === 'teacher'
                    ? lesson.teacherJoinedAt
                    : lesson.studentJoinedAt,
                seconds:
                  role === 'teacher'
                    ? lesson.teacherSeconds
                    : lesson.studentSeconds,
              }))}
              columns={[
                { title: '参与者', dataIndex: 'name' },
                {
                  title: '出勤',
                  render: (_, row) => <Tag color={row.color}>{row.label}</Tag>,
                },
                {
                  title: '首次进入时间',
                  dataIndex: 'joined',
                  render: formatTime,
                },
                { title: '在教室时长', dataIndex: 'seconds', render: duration },
              ]}
            />
            <Descriptions size="small" column={2} style={{ marginTop: 16 }}>
              <Descriptions.Item label="实际开课">
                {formatTime(lesson.classStartedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="实际结束">
                {formatTime(lesson.classEndedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="缺席原因" span={2}>
                {lesson.absentReason || '—'}
              </Descriptions.Item>
            </Descriptions>
            <Typography.Text type="secondary">
              迟到沿用现有规则：晚于计划上课时间 5
              分钟。到课时间未知时不判为准时。
            </Typography.Text>
          </Card>
          <Card
            title={`课程总结 · ${summaryLabels[lesson.summaryStatus]}`}
            size="small"
          >
            <Descriptions size="small" column={2}>
              <Descriptions.Item label="首次有效提交">
                {formatTime(lesson.summarySubmittedAt)}
              </Descriptions.Item>
              <Descriptions.Item label="提交截止">
                {formatTime(lesson.summaryDueAt)}
              </Descriptions.Item>
              <Descriptions.Item label="学生评价" span={2}>
                {data.studentEvaluation || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="词汇" span={2}>
                {data.words.join('、') || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="朗读句子" span={2}>
                {data.sentences.join('\n') || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="语法要点" span={2}>
                {data.grammarPoints || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="作业" span={2}>
                {data.homework || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="下节课计划" span={2}>
                {data.nextClassPlan || '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
          <Card title="网络状况" size="small">
            <Typography.Paragraph type="secondary">
              诊断采样保留 {data.networkRetentionDays}{' '}
              天；历史课堂或未上传的一方显示无数据。网络波动表示出现重连采样、质量等级
              ≥ 4、往返延迟 ≥ 400ms 或丢包率 ≥ 5%，不代表整节课持续异常。
            </Typography.Paragraph>
            <Table<LessonNetwork>
              size="small"
              rowKey="role"
              pagination={false}
              scroll={{ x: 720 }}
              dataSource={(['teacher', 'student'] as const).map(
                (role) =>
                  lesson.network.find((item) => item.role === role) ?? {
                    role,
                    samples: 0,
                    status: 'UNKNOWN',
                    maxRttMs: null,
                    maxPacketLossRatio: null,
                    worstQuality: null,
                    reconnectSamples: 0,
                    lastSampleAt: null,
                  },
              )}
              columns={[
                {
                  title: '参与者',
                  dataIndex: 'role',
                  render: (role) => (role === 'teacher' ? '老师' : '学生'),
                },
                {
                  title: '状况',
                  render: (_, row) =>
                    row.samples ? networkLabels[row.status] : '无数据',
                },
                { title: '采样数', dataIndex: 'samples' },
                {
                  title: '最大往返延迟',
                  dataIndex: 'maxRttMs',
                  render: (value) =>
                    value == null ? '—' : `${Math.round(value)}ms`,
                },
                {
                  title: '最大丢包率',
                  dataIndex: 'maxPacketLossRatio',
                  render: (value) =>
                    value == null ? '—' : `${(value * 100).toFixed(1)}%`,
                },
                { title: '重连采样数', dataIndex: 'reconnectSamples' },
                {
                  title: '最后采样',
                  dataIndex: 'lastSampleAt',
                  render: formatTime,
                },
              ]}
            />
          </Card>
          <Card title="课堂回放" size="small">
            {data.recordings.length ? (
              data.recordings.map((url, index) => (
                <div key={url} style={{ marginBottom: 16 }}>
                  <Typography.Link
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    回放 {index + 1}
                  </Typography.Link>
                  {/\.mp4(?:[?#]|$)/i.test(url) && (
                    <video
                      controls
                      preload="none"
                      src={url}
                      style={{ display: 'block', width: '100%', marginTop: 8 }}
                    />
                  )}
                </div>
              ))
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="暂无可用回放"
              />
            )}
          </Card>
        </>
      )}
    </Space>
  )
}
